import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, IconButton, List, Paper, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from '../../contexts/TransactionContext';
import { useDemoTransaction } from '../onboarding/DashboardTour';
import { Transaction, BudgetCategory } from '../../types';
import NewTransactionDialog from '../transactions/NewTransactionDialog';
import { DraggableTransaction } from './DraggableTransaction';
import { transactions } from '../../services/api';
import { useBudget } from '../../contexts/BudgetContext';

const RecentTransactions: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const demoTransaction = useDemoTransaction();
  const { currentBudget, setCurrentBudget } = useBudget();
  const { 
    recentTransactions, 
    pagination, 
    loadMoreTransactions,
    categoryFilter,
    setCategoryFilter,
    refreshTransactions
  } = useTransactions();
  const [isNewTransactionDialogOpen, setIsNewTransactionDialogOpen] = useState(false);

  // Debug logging
  console.log('Recent Transactions:', {
    recentCount: recentTransactions.length,
    totalCount: pagination?.totalCount || 0,
    pagination,
    recentTransactions
  });

  // Combine real transactions with demo transaction if available
  const displayTransactions = React.useMemo(() => {
    if (!demoTransaction) return recentTransactions;
    const demoWithId = {
      ...demoTransaction,
      _id: 'demo-transaction',
      user: 'demo-user',
      createdAt: new Date().toISOString()
    } as Transaction;
    return [demoWithId, ...recentTransactions];
  }, [recentTransactions, demoTransaction]);

  const handleCreateTransaction = async (newTransaction: Omit<Transaction, '_id' | 'user' | 'createdAt'>) => {
    try {
      await transactions.create(newTransaction);
      setIsNewTransactionDialogOpen(false);

      // Update budget amounts
      if (currentBudget) {
        const updatedBudget = {
          ...currentBudget,
          categories: currentBudget.categories.map((category: BudgetCategory) => {
            if (category.name === newTransaction.category && category.type === newTransaction.type) {
              return {
                ...category,
                actualAmount: category.actualAmount + newTransaction.amount
              };
            }
            return category;
          })
        };
        setCurrentBudget(updatedBudget);
      }

      await refreshTransactions();
    } catch (err) {
      console.error('Error creating transaction:', err);
    }
  };

  const hasMoreTransactions = pagination ? displayTransactions.length < pagination.totalCount : false;

  const handleShowMore = () => {
    setCategoryFilter(null); // Clear the filter
    loadMoreTransactions(); // Load more transactions
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.6)})`
          : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)})`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Recent Transactions
        </Typography>
        <Tooltip title="View all transactions">
          <IconButton 
            size="small" 
            sx={{ color: theme.palette.primary.main }}
            onClick={() => navigate('/transactions')}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <List 
        sx={{ 
          '& > *:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setIsNewTransactionDialogOpen(true)}
            startIcon={<AddIcon />}
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: 2,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: 'primary.main',
              backgroundColor: 'transparent',
              color: 'primary.main',
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: 'primary.main',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                transform: 'translateY(-1px)',
                boxShadow: theme => theme.palette.mode === 'light' 
                  ? '0 4px 12px 0 rgba(124, 139, 111, 0.25)'
                  : '0 4px 12px 0 rgba(157, 171, 142, 0.3)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              }
            }}
          >
            Add New Transaction
          </Button>
        </Box>
        {displayTransactions.map((transaction: Transaction) => (
          <DraggableTransaction
            key={transaction._id}
            transaction={transaction}
          />
        ))}
      </List>

      {hasMoreTransactions && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="text"
            onClick={handleShowMore}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            Show More
          </Button>
        </Box>
      )}

      <NewTransactionDialog
        open={isNewTransactionDialogOpen}
        onClose={() => setIsNewTransactionDialogOpen(false)}
        onSubmit={handleCreateTransaction}
      />
    </Paper>
  );
};

export default RecentTransactions; 